import { ColumnDef } from '@tanstack/react-table';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Box } from '@/lib/api/boxes';
import { apkLabelClass } from '@/lib/api/apks';
import { BoxRowActions, LiveViewControls } from './box-row-actions';

interface BoxTableMeta {
  isAdmin?: boolean;
  liveIps?: Set<string>;
  /** Labels of labeled APKs whose name/file embeds this app version. */
  labelsForVersion?: (version?: string) => string[];
}

export const columns: ColumnDef<Box>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomeRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <div
        onClick={() =>
          column.toggleSorting(column.getIsSorted() == 'asc', false)
        }
      >
        Name
      </div>
    ),
    cell: ({ row }) => <div className="capitalize">{row.getValue('name')}</div>,
  },
  {
    accessorKey: 'ip',
    header: 'IP',
    cell: ({ row }) => <div className="capitalize">{row.getValue('ip')}</div>,
  },
  {
    accessorKey: 'group',
    header: 'Group',
    cell: ({ row }) => <div>{(row.getValue('group') as string) || '—'}</div>,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: 'tv',
    header: 'TV',
    cell: ({ row }) => <div>{(row.getValue('tv') as string) || '—'}</div>,
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge className="capitalize">{row.getValue('status')}</Badge>
    ),
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'brand',
    header: 'Brand',
    cell: ({ row }) => <div>{row.getValue('brand')}</div>,
    filterFn: (row, id, value) => {
      return value.includes(row.getValue(id));
    },
  },
  {
    accessorKey: 'androidVersion',
    header: 'Android',
    cell: ({ row }) => (
      <div>{(row.getValue('androidVersion') as string) || '—'}</div>
    ),
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    accessorKey: 'appVersion',
    header: 'App',
    // When the version matches a labeled APK, show the label bubble instead
    // of the raw version (which stays in the tooltip). Sort/filter still use
    // the underlying version value.
    cell: ({ row, table }) => {
      const version = (row.getValue('appVersion') as string) || '';
      if (!version) return <div>—</div>;
      const meta = table.options.meta as BoxTableMeta;
      const labels = meta?.labelsForVersion?.(version) ?? [];
      if (!labels.length) return <div>{version}</div>;
      return (
        <div className="flex flex-wrap gap-1" title={version}>
          {labels.map((label) => (
            <Badge
              key={label}
              variant="outline"
              className={apkLabelClass(label)}
            >
              {label}
            </Badge>
          ))}
        </div>
      );
    },
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
  {
    id: 'actions',
    header: '',
    enableSorting: false,
    enableHiding: false,
    cell: ({ row, table }) => {
      const meta = table.options.meta as BoxTableMeta;
      const ip = row.original.ip;
      const isLive = meta?.liveIps?.has(ip) ?? false;
      return (
        <div className="flex items-center justify-end gap-1">
          <LiveViewControls ip={ip} isLive={isLive} />
          {meta?.isAdmin && <BoxRowActions box={row.original} />}
        </div>
      );
    },
  },
];
